import { useCurrentWorkspace, useCustomQuery } from '@gripp/shared-logic';
import { rangeToDates } from './useHomeActivityMap';
import { TimeRange } from '../types';

/**
 * Status-segmented counts for a location's activities.
 * Mirrors the generated `HomeStatusCounts`
 * (backend-workspace/apps/gripp-api/generated/index.ts) — schema-first.
 */
export interface HomeStatusCounts {
  total: number;
  urgent: number;
  pending: number;
  solved: number;
}

/**
 * A located site for the OEM Home map with its rolled-up counts.
 * Mirrors the generated `LocationSummary` — a read projection of the (future)
 * Location model, served from a mock seed in Phase 1.5. The pill shows `name`
 * only; clicking a pin reveals the counts. `activities` is range-scoped;
 * `assets` / `routines` are present-state.
 */
export interface LocationSummary {
  id: string;
  name: string;
  lat: number;
  lng: number;
  activities: HomeStatusCounts;
  assets: number;
  routines: number;
}

const HOME_LOCATIONS_QUERY = `
  query HomeLocations($filter: HomeActivityFilter) {
    homeLocations(filter: $filter) {
      id
      name
      lat
      lng
      activities {
        total
        urgent
        pending
        solved
      }
      assets
      routines
    }
  }
`;

/**
 * Fetches the located sites (with rolled-up counts) for the OEM Home map,
 * scoped to the current workspace and the selected time range.
 *
 * Root query — reads `data.homeLocations` directly (no unwrap). Guards
 * `currentWorkspace?.id` before issuing (undefined on first render).
 *
 * Phase 1.5: backed by a mock resolver, so the set is bounded and the activity
 * counts scale with the range while assets/routines stay constant. `rangeToDates`
 * is shared with `useHomeActivityMap` — do NOT re-implement.
 */
export function useHomeLocations(range: TimeRange): {
  data: LocationSummary[] | undefined;
  isLoading: boolean;
  error: Error | null;
} {
  const workspace = useCurrentWorkspace();
  const workspaceId = workspace?.id;
  const { startDate, endDate } = rangeToDates(range);

  const { data, isLoading, error } = useCustomQuery({
    cacheKey: ['homeLocations', workspaceId, range],
    query: HOME_LOCATIONS_QUERY,
    variables: {
      filter: {
        ...(startDate ? { startDate } : {}),
        ...(endDate ? { endDate } : {}),
      },
    },
    options: {
      // Do not issue the query until the workspace id is known.
      enabled: !!workspaceId,
      staleTime: 60_000,
    },
  });

  const locations: LocationSummary[] | undefined = data?.homeLocations;

  return { data: locations, isLoading, error: error as Error | null };
}
